import { Capacitor } from "@capacitor/core"
import { Filesystem } from "@capacitor/filesystem"
import type { Photo } from "@capacitor/camera"

function base64Blob(data: string, type: string): Blob {
  const bytes = atob(data)
  const buffer = new Uint8Array(bytes.length)
  for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i)
  return new Blob([buffer], { type })
}

function validateImage(blob: Blob): Blob {
  if (!blob.size || (blob.type && !blob.type.startsWith("image/") && blob.type !== "application/octet-stream")) {
    throw new Error("O arquivo recebido não é uma imagem válida.")
  }
  return blob
}

/**
 * No APK, o `webPath` do plugin nativo (`https://localhost/_capacitor_file_/...`)
 * nem sempre é lido de forma confiável por `fetch()` dentro da WebView — por isso
 * lemos os bytes direto pelo `Filesystem`. No navegador (CameraWeb), `webPath` é
 * um `blob:` local ao documento e `fetch()` funciona normalmente.
 */
export async function cameraPhotoToFile(photo: Photo): Promise<File> {
  const format = photo.format === "jpeg" ? "jpg" : photo.format
  const type = `image/${photo.format === "jpg" ? "jpeg" : photo.format}`
  let blob: Blob
  if (Capacitor.isNativePlatform() && photo.path) {
    const result = await Filesystem.readFile({ path: photo.path })
    blob = typeof result.data === "string" ? base64Blob(result.data, type) : result.data
  } else {
    if (!photo.webPath) throw new Error("A câmera não retornou o arquivo da foto.")
    const response = await fetch(photo.webPath)
    if (!response.ok) throw new Error("Não foi possível ler a foto capturada.")
    blob = await response.blob()
  }
  // `Filesystem.readFile` no APK pode devolver um Blob sem `.type` (string vazia).
  // `Blob.type` é imutável, e o backend rejeita upload com Content-Type vazio
  // (`UploadBlobCommandValidator`, 400 VALIDATION) — reconstrói com o MIME certo
  // quando faltar.
  if (!blob.type) blob = new Blob([blob], { type })
  validateImage(blob)

  // `cordova-plugin-file` (dependência do @awesome-cordova-plugins/media-capture,
  // usado pelo VideoField) sobrescreve a classe global `File`
  // (`<clobbers target="window.File" />`) por um shim não-nativo do Cordova.
  // `new File(...)` passaria a devolver esse shim, que `URL.createObjectURL`
  // rejeita ("Overload resolution failed") em `uploadAndCommit`. Mesmo
  // contorno já usado em VideoField.tsx: Blob nativo + `name` simulado.
  const file = blob as unknown as File
  const fileName = `photo_${Date.now()}.${format}`
  try {
    Object.defineProperty(file, "name", { value: fileName, writable: false })
  } catch {
    // Ignora se o Blob não for extensível no motor nativo — uploadBlob/compressImage
    // não dependem de `.name` para funcionar, só o relatório/preview usam.
  }
  return file
}

export async function fetchMediaBlob(url: string, headers: Record<string, string>, signal: AbortSignal): Promise<Blob> {
  const response = await fetch(url, { headers, signal })
  if (!response.ok) throw new Error(`Unable to load media (HTTP ${response.status}).`)
  const blob = await response.blob()
  const type = typeof blob.type === "string" ? blob.type.split(";", 1)[0].trim().toLowerCase() : ""
  if (!blob.size || (type && !/^(image|video)\//.test(type) && !["application/octet-stream", "binary/octet-stream", "application/binary"].includes(type))) {
    throw new Error("The response is not a nonempty image or video file.")
  }
  return blob
}

export async function fetchImageBlob(url: string, headers: Record<string, string>, signal: AbortSignal): Promise<Blob> {
  return validateImage(await fetchMediaBlob(url, headers, signal))
}
